import React from 'react'
import { Box, Paper, Row, Col, Chip, sx } from '@applift/factor'
import { RadioButtonChecked, RadioButtonUnchecked } from '@applift/icons'
import { DataGrid, RowSelectionState, SortingState } from '@applift/datagrid'
import { ConversionFilter, conversionFilterIdMap } from '../../models/List'
import { Search } from '../Search'
import { NoResultsOverlay } from '../NoResultsOverlay'
import { getColumnsOrder, getColumns } from './colDef'
import { defaultListSorting } from '../../constants/list'
import { useSelectConversionsTableData } from '../../hooks/useCmpConversions'
import { useMeasurementPartnersList } from '../../hooks/usePostback'
import { PostbackPartner } from '../../models/Postback'

const PAGE_SIZE = 20

interface Props {
  initialSelection: RowSelectionState
  conversionType: Omit<ConversionFilter, 'any'>
  excludeConversionIds: number[]
  owId: number
  onSelectRows: (rows: RowSelectionState) => void
  onError: (e: any) => void
  tableHeight: string
  tableMaxHeight?: string
  tableParentClassName?: string
}

export const ConversionSelectionTable = (props: Props) => {
  const {
    excludeConversionIds,
    owId,
    conversionType,
    initialSelection,
    onSelectRows,
    onError,
    tableHeight,
    tableMaxHeight,
    tableParentClassName,
  } = props

  const [sorting, setSorting] = React.useState<SortingState>(defaultListSorting)
  const [columnOrder, setColumnOrder] = React.useState<string[]>([])
  const [rowSelection, setRowSelection] =
    React.useState<RowSelectionState>(initialSelection)
  const [search, setSearch] = React.useState('')
  const [totalRecords, setTotalRecords] = React.useState<number | undefined>()

  React.useEffect(() => {
    onSelectRows(rowSelection)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection])

  React.useEffect(() => {
    setColumnOrder(getColumnsOrder(conversionType))
  }, [conversionType])

  const handleClearRowSelection = () => {
    setRowSelection({})
  }

  const selectedConversionIds = React.useMemo(
    () =>
      Object.entries(rowSelection || {})
        .filter(([key, _value]) => !!rowSelection[key])
        .map(entry => entry[0]),
    [rowSelection]
  )

  const rowSelectedLength = React.useMemo(
    () => selectedConversionIds?.length || 0,
    [selectedConversionIds]
  )

  const { data, isLoading, fetchNextPage } = useSelectConversionsTableData(
    excludeConversionIds,
    owId,
    conversionFilterIdMap[conversionType as string] || 1,
    sorting,
    search,
    PAGE_SIZE,
    { onError }
  )

  const postbackPartnerArr = useMeasurementPartnersList()

  const postbackPartnerMapper = React.useMemo(() => {
    const mapper = {} as { [key: string]: PostbackPartner }
    postbackPartnerArr?.forEach(
      postbackPartner => (mapper[postbackPartner.id] = postbackPartner)
    )
    return mapper
  }, [postbackPartnerArr])

  const flatData = React.useMemo(() => {
    return data?.pages?.map(page => page.data.data).flat(1) || []
  }, [data])

  const filteredRecords = data?.pages[0]?.data.filteredRecords

  React.useEffect(() => {
    const total = data?.pages[0]?.data.totalRecords
    if (total) {
      setTotalRecords(total)
    }
  }, [data])

  const colDef = React.useMemo(
    () => getColumns(postbackPartnerMapper, conversionType),
    [conversionType, postbackPartnerMapper]
  )

  const overlay = React.useMemo(() => {
    return !flatData?.length && !isLoading ? 'noResult' : undefined
  }, [flatData, isLoading])

  const disableMultipleRowSelection = conversionType?.toLowerCase() !== 'pixel'

  const onFetchRows = React.useCallback(() => {
    setTimeout(fetchNextPage, 100)
  }, [fetchNextPage])

  const setRowSelectionWrapper = React.useCallback(
    (value: any) =>
      setRowSelection({ ...(typeof value === 'function' ? value() : value) }),
    [setRowSelection]
  )

  if (!conversionType) {
    return null
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 100,
      }}
    >
      <Row>
        <Col xs={12}>
          <Search
            search={search}
            setSearch={setSearch}
            placeholder="Search by ID, Name"
            debounce={300}
            sx={{ marginY: 16 }}
          />
          <Paper
            variant="elevation"
            elevation={2}
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {conversionType.toLowerCase() === 'pixel' && (
              <Row
                sx={{
                  p: 16,
                  visibility:
                    rowSelectedLength > 0 && totalRecords
                      ? 'visible'
                      : 'hidden',
                }}
              >
                <Col xs={12}>
                  <Chip
                    label={`${rowSelectedLength} of ${totalRecords} pixels selected`}
                    onDelete={
                      rowSelectedLength > 0
                        ? handleClearRowSelection
                        : undefined
                    }
                    color="secondary"
                  />
                </Col>
              </Row>
            )}
            <Row
              sx={{
                ...(conversionType.toLowerCase() === 'postback'
                  ? { mt: 40 }
                  : {}),
              }}
            >
              <Col xs={12}>
                <Box
                  style={{
                    height: tableHeight || '350px',
                    maxHeight: tableMaxHeight,
                  }}
                  className={tableParentClassName || ''}
                >
                  <DataGrid
                    data={flatData || []}
                    columns={colDef}
                    getRowId={(row: any) => `${row.id}`}
                    components={{
                      NoResultsOverlay: NoResultsOverlay,
                    }}
                    overlay={overlay}
                    pageSize={PAGE_SIZE}
                    hideHeader
                    hideFooter
                    density="compact"
                    loading={isLoading}
                    showColumnRightBorder
                    showCellRightBorder
                    disableRowSelectionOnClick
                    disableExtendRowFullWidth
                    rowCount={filteredRecords}
                    onFetchRows={onFetchRows}
                    classes={{
                      root: sx({
                        borderRadius: 0,
                        border: 0,
                        borderTop: 1,
                      }),
                    }}
                    state={{ sorting, columnOrder, rowSelection }}
                    onSortingChange={setSorting}
                    enableRowSelection
                    disableMultipleRowSelectionWithCheckboxSelection={
                      disableMultipleRowSelection
                    }
                    componentsProps={{
                      ...(disableMultipleRowSelection
                        ? {
                            baseCheckbox: {
                              icon: <RadioButtonUnchecked />,
                              checkedIcon: <RadioButtonChecked />,
                              indeterminateIcon: <RadioButtonChecked />,
                            },
                          }
                        : {}),
                    }}
                    onRowSelectionChange={setRowSelectionWrapper}
                    checkboxSelection={true}
                    disableSelectAll={disableMultipleRowSelection}
                  />
                </Box>
              </Col>
            </Row>
          </Paper>
        </Col>
      </Row>
    </Box>
  )
}
